import type { Element } from 'hast';
import type { ChartWidgetProps, ChartWidgetTemplate } from '../types';

/**
 * Разбор блока графика из контента CMS.
 *
 * Конструктор в админке (Контент АТОН → Виджеты для публикаций → Графики)
 * отдаёт редактору готовый блок:
 *
 * ```html
 * <div data-charts="am_fund_11,AKCH" data-template="default"
 *      data-period="month_12" data-currency="rub"></div>
 * ```
 *
 * `data-template` — фронтовая настройка, в API не участвует: она выбирает
 * активную вкладку. Остальные атрибуты уходят в запрос как есть.
 */

/** Шаблоны, которые умеет виджет. Список продублирован в админке. */
const TEMPLATES: ChartWidgetTemplate[] = ['default', 'scha'];

/** hast хранит `data-charts` как `dataCharts` — берём строковое значение. */
const readAttr = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** Элемент контента — блок графика. */
export const isChartElement = (el: Element): boolean =>
  el.tagName === 'div' && Boolean(readAttr(el.properties?.dataCharts));

/**
 * Data-атрибуты блока → пропсы виджета. `null` — блок не про график
 * или коды не заданы, тогда элемент рендерится как обычный `div`.
 */
export function parseChartAttrs(el: Element): ChartWidgetProps | null {
  const properties = el.properties ?? {};
  const charts = readAttr(properties.dataCharts)
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  if (!charts.length) return null;

  const template = readAttr(properties.dataTemplate) as ChartWidgetTemplate;

  return {
    charts,
    template: TEMPLATES.includes(template) ? template : 'default',
    period: readAttr(properties.dataPeriod) || undefined,
    start: readAttr(properties.dataStart) || undefined,
    end: readAttr(properties.dataEnd) || undefined,
    currency: readAttr(properties.dataCurrency) || undefined,
    title: readAttr(properties.dataTitle) || undefined,
  };
}
